import { getEnv } from 'common-nextjs';
import { formatFormikError } from 'fetcher-session';
import { Form, Formik } from 'formik';
import {
  FormikErrorMessage,
  FormikField,
  FormikLoader,
  FormikPasswordField,
} from 'formik-ui';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { useCookie, useMount } from 'react-use';
import { Button } from 'ui';
import * as yup from 'yup';
import NewTabLink from '~/components/Links/NewTabLink';
import { generateRegisterOnboardingLink } from '~/components/Links/RegisterLink';
import AuthButtons from '~/components/LoginRegister/AuthButtons';
import ReferralCodeGuidance from '~/components/LoginRegister/ReferralCodeGuidance';
import useAuthService from '~/lib/session/useAuthService';
import { AnalyticsSource } from '~/services/analytics/AnalyticsInterfaces';
import { visitedEmailJoin } from '~/services/analytics/events/auth';
import { SocialAuthCallback } from '~/typings/services/iam/auth';
import {
  PASSWORD_MINIMUM,
  PASSWORD_SCHEMA,
  USERNAME_MAXIMUM,
  USERNAME_SCHEMA,
} from '../../constants/validation';

export interface RegisterFormValues {
  email: string;
  username: string;
  password: string;
  referral_code?: string;
}

const schema: yup.SchemaOf<RegisterFormValues> = yup.object({
  email: yup.string().required('Required').email('Must be an email'),
  username: USERNAME_SCHEMA,
  password: PASSWORD_SCHEMA,
  referral_code: yup.string(),
});

interface Props {
  authButtons?: boolean;
  onSocialAuthNewUser?: SocialAuthCallback;
  onSocialAuthExistingEmail?: SocialAuthCallback;
  onSuccessfulRegister?: () => void;
  postContent?: React.ReactNode;
  src?: AnalyticsSource;
}

const RegisterForm: React.FC<Props> = ({
  authButtons,
  onSocialAuthExistingEmail,
  onSocialAuthNewUser,
  onSuccessfulRegister,
  postContent,
  src,
}) => {
  const router = useRouter();
  const { tryRegister } = useAuthService();
  const [referralCodeCookie] = useCookie('referral_code');
  const [referralCodeExpanded, setReferralCodeExpanded] =
    useState(!!referralCodeCookie);

  useMount(() => {
    visitedEmailJoin(src);
    router.prefetch(
      generateRegisterOnboardingLink({ destination: router.asPath }),
    );
  });

  return (
    <Formik
      initialValues={
        {
          email: '',
          username: '',
          password: '',
          referral_code: referralCodeCookie || '',
        } as RegisterFormValues
      }
      onSubmit={async (values, formikHelpers) => {
        try {
          await tryRegister(
            values.email,
            values.username,
            values.password,
            values.referral_code,
            {
              onSuccessfulRegister,
            },
          );
        } catch (err) {
          formikHelpers.setErrors(formatFormikError<RegisterFormValues>(err));
        }
      }}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={schema}
    >
      {props => (
        <FormikLoader>
          {authButtons && (
            <AuthButtons
              onSocialAuthExistingEmail={onSocialAuthExistingEmail}
              onSocialAuthNewUser={onSocialAuthNewUser}
              onSuccessfulLogin={onSuccessfulRegister}
            />
          )}

          <Form className="mt-4">
            <FormikField
              name="email"
              label="Email"
              autoComplete="email"
              autoCapitalize="no"
              autoCorrect="no"
              autoFocus
            />
            <FormikField
              name="username"
              label="Username"
              autoComplete="username"
              autoCapitalize="no"
              autoCorrect="no"
              hint={`No longer than ${USERNAME_MAXIMUM} characters, no spaces, no special characters`}
            />
            <FormikPasswordField
              name="password"
              label="Password"
              autoComplete="new-password"
              autoCapitalize="no"
              autoCorrect="no"
              hint={`${PASSWORD_MINIMUM} characters or more`}
            />

            {referralCodeExpanded ? (
              <>
                <ReferralCodeGuidance
                  referralCode={props.values.referral_code}
                />

                <FormikField
                  name="referral_code"
                  label="Referral Code (optional)"
                />
              </>
            ) : (
              <div className="mb-4 text-left">
                <button
                  className="text-sm font-semibold underline"
                  type="button"
                  onClick={() => setReferralCodeExpanded(true)}
                >
                  Have a referral code?
                </button>
              </div>
            )}

            <FormikErrorMessage />

            <p className="text-slate-green-500 my-4 text-center text-xs">
              By clicking Register, I agree to SidelineSwap&apos;s{' '}
              <NewTabLink href="/terms" className="text-blue-500">
                Terms of Service
              </NewTabLink>{' '}
              and{' '}
              <NewTabLink href="/privacy" className="text-blue-500">
                Privacy Policy
              </NewTabLink>
            </p>

            {getEnv() !== 'production' && (
              <Button
                type="button"
                className="mb-2"
                variant="text"
                color="black"
                onClick={() => {
                  const username = `user${Date.now()}`.slice(
                    0,
                    USERNAME_MAXIMUM,
                  );

                  props.setValues({
                    username: username,
                    password: username,
                    email: `mailtrap+${username}@sidelineswap.com`,
                    referral_code: props.values.referral_code,
                  });
                }}
              >
                Generate User
              </Button>
            )}

            <Button type="submit" fluid className="mb-4">
              Register
            </Button>
          </Form>

          {postContent}
        </FormikLoader>
      )}
    </Formik>
  );
};

export default RegisterForm;
